/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentProps } from 'preact';
import { Menu, MenuItem } from '../UNSAFE_Menu';
import { TabBarContextMenuConfig, TabBarItemContext } from '../UNSAFE_TabBarCommon';
import { TabBar } from './TabBar';
import { Separator } from '../UNSAFE_Separator';
import { TabBarItemContextMenuContext } from '../UNSAFE_TabBarCommon/TabBar.types';
import { ReorderableTabBar } from '../UNSAFE_ReorderableTabBar';
import { handleMove } from '../utils/PRIVATE_tabBarUtils';

export type Props<K extends string | number> = Omit<ComponentProps<typeof Menu>, 'children'> & {
  contextMenuConfig: TabBarContextMenuConfig<K>;
} & { contextMenuContext: TabBarItemContext<K> };

/**
 * The internal component used to render a custom or default context menu
 */
export function TabBarContextMenu<K extends string | number>({
  contextMenuConfig,
  contextMenuContext,
  ...menuProps
}: Props<K>) {
  const accessibleLabel = contextMenuConfig.accessibleLabel;

  // We want to call itemsRenderer only when menu is open otherwise it would be called during render phase when no context is available
  const menuItems = menuProps.isOpen ? contextMenuConfig.itemsRenderer(contextMenuContext) : null;
  // If there are menu items then we construct the menu otherwise menu will be mounted unnecessarily, if menu items are null we return an empty fragment as return type needs to be a JSX
  if (menuItems != null) {
    return (
      <>
        <Menu
          {...menuProps}
          {...(accessibleLabel && {
            'aria-label': accessibleLabel
          })}>
          {menuItems}
        </Menu>
      </>
    );
  }
  return <></>;
}

/**
 * Optional props because we could have only remove feature or reorder feature.
 */
type StatusProps<K extends string | number> = {
  position?: 'before' | 'after';
  destinationKey?: K;
  removedKey?: K;
};
type AccInfo = {
  /*
   * Callback to obtain the key for remove/ key and position for moving a tab and build acc status text.
   */
  handleAccStatus: (statusDetail: StatusProps<string | number>) => void;
};

/**
 * Optional because we could have only remove feature or reorder feature.
 */
type RemoveItemProps = {
  handleRemove?: ComponentProps<typeof TabBar>['onRemove'];
  isRemovable?: boolean;
};

/**
 * Optional because we could have only remove feature or reorder feature.
 */
type ReorderItemProps<K> = {
  handleReorder?: ComponentProps<typeof ReorderableTabBar>['onReorder'];
  tabKeys?: K[];
  direction?: 'ltr' | 'rtl';
};

type TranslationLabels = {
  /*
   * The translation labels for remove item and move items. Optional because we could have only remove feature or reorder feature.
   */
  itemLabels: { labelRemove?: string; labelMoveLeft?: string; labelMoveRight?: string };
};

/**
 * Creates the default context menu items
 */
export function getDefaultContextMenu<K extends string | number>({
  isRemovable,
  handleReorder,
  handleRemove,
  itemKey,
  tabKeys,
  direction,
  itemLabels,
  handleAccStatus
}: RemoveItemProps & ReorderItemProps<K> & TabBarItemContext<K> & TranslationLabels & AccInfo) {
  const handleRemoveAction = () => {
    handleRemove?.({ value: itemKey as string | number });
    handleAccStatus?.({
      removedKey: itemKey
    });
  };

  const isReorderable = handleReorder != null;

  const isRtl = direction === 'rtl';
  const handleMoveAction = (moveDirection: 'forward' | 'backward') => {
    if (isReorderable && tabKeys) {
      const { newArray, referenceItemIndex } = handleMove(moveDirection, tabKeys, itemKey);
      const destinationKey = tabKeys[referenceItemIndex];
      handleReorder?.({
        reorderedKeys: newArray,
        itemKeys: [itemKey],
        referenceKey: destinationKey
      });
      handleAccStatus?.({
        position: moveDirection === 'forward' ? 'after' : 'before',
        destinationKey: destinationKey
      });
    }
  };

  const allMenuItems = isReorderable
    ? {
        moveForwardItem: (
          <MenuItem
            label={isRtl ? itemLabels?.labelMoveLeft! : itemLabels?.labelMoveRight!}
            onAction={() => handleMoveAction('forward')}
          />
        ),
        moveBackwardItem: (
          <MenuItem
            label={isRtl ? itemLabels?.labelMoveRight! : itemLabels?.labelMoveLeft!}
            onAction={() => handleMoveAction('backward')}
          />
        ),
        removeItem: <MenuItem label={'Remove'} onAction={handleRemoveAction} />,
        remove: <MenuItem label={'Remove'} onAction={handleRemoveAction} />,
        reorder: (
          <>
            <MenuItem
              label={isRtl ? itemLabels?.labelMoveLeft! : itemLabels?.labelMoveRight!}
              onAction={() => handleMoveAction('forward')}
            />
            <MenuItem
              label={isRtl ? itemLabels?.labelMoveRight! : itemLabels?.labelMoveLeft!}
              onAction={() => handleMoveAction('backward')}
            />
          </>
        )
      }
    : {
        remove: <MenuItem label={'Remove'} onAction={handleRemoveAction} />,
        removeItem: <MenuItem label={'Remove'} onAction={handleRemoveAction} />
      };

  const createMoveMenuItems = (itemKey: K) => {
    let moveItem;
    switch (isReorderable) {
      case itemKey === tabKeys?.[0]:
        moveItem = allMenuItems['moveForwardItem'];
        break;
      case itemKey === tabKeys?.[tabKeys.length - 1]:
        moveItem = allMenuItems['moveBackwardItem'];
        break;
      default:
        moveItem = (
          <>
            {allMenuItems['moveForwardItem']}
            {allMenuItems['moveBackwardItem']}
          </>
        );
    }
    return moveItem;
  };

  const defaultMenuItems =
    isRemovable || isReorderable ? (
      <>
        {isReorderable && tabKeys ? (
          <>
            {createMoveMenuItems(itemKey)}
            {isRemovable ? <Separator /> : undefined}
          </>
        ) : undefined}
        {isRemovable ? allMenuItems['remove'] : undefined}
      </>
    ) : undefined;

  return {
    itemKey,
    defaultMenuItems,
    allMenuItems
  } as TabBarItemContextMenuContext<K>;
}
